var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

import _ from 'lodash';
import { findNearestColor } from './color';
import { collapsedConcat, nl } from './utils';

function parseStyle(node) {
  if (node.kind === 'text') {
    return {};
  }

  var styleAttr = node.attrs.find(function (v) {
    return v.name === 'style';
  });

  if (!styleAttr) {
    return {};
  }

  var styles = styleAttr.value.split(';').map(_.trim).filter(Boolean).map(function (s) {
    return s.split(':');
  }).map(function (_a) {
    var a = _a[0],
        b = _a[1];
    return [_.trim(a), _.trim(b)];
  }).filter(function (v) {
    return v.length === 2;
  });
  return _.fromPairs(styles);
}

function findAttr(node, field) {
  if (node.kind === 'text') {
    return undefined;
  }

  return node.attrs.find(function (attr) {
    return attr.name === field;
  });
}

function getNumAttr(node, attr) {
  if (node.kind === 'text') {
    return null;
  }

  var attrObj = node.attrs.find(function (v) {
    return v.name === attr;
  });

  if (!attrObj) {
    return null;
  }

  return attrObj.value;
} // Convert HTML tree to GenerationTree, which includes indication to stringify the node tree.
// In terms of implementation, this function does not remove empty nodes, but handles trimming.
// tslint:disable-next-line:max-func-body-length cyclomatic-complexity


export function bbsTreeToGenerationTree(node) {
  if (node.kind === 'text') {
    return node.text.replace(/(^\s{2,})|(\s{2,}$)|(^\t|\t$)/, "");
  } // Node types to skip


  if (_.includes(['style', 'script', '#comment'], node.tag)) {
    return '';
  }

  var style = parseStyle(node);
  var children = node.children.map(bbsTreeToGenerationTree); // <picture>: pick only the first node child

  if (node.tag === 'picture') {
    var firstNode = _.find(children, function (child) {
      return typeof child !== 'string';
    });

    children = firstNode ? [firstNode] : [];
  } // Wrap children with styles


  if (style.color) {
    var ngaColor = findNearestColor(style.color);
    children = [{
      tag: 'color',
      tagSuffix: "=" + ngaColor,
      children: children
    }];
  }

  switch (node.tag) {
    case 'br':
      return {
        keepEmpty: true,
        newlines: {
          outSum: 1
        }
      };

    case 'hr':
      return {
        children: ['======'],
        keepEmpty: true,
        newlines: {
          outBefore: 2,
          outAfter: 2
        }
      };

    case 'figure':
      return {
        children: children,
        newlines: {
          outBefore: 1,
          outAfter: 1
        }
      };

    case 'table':
      return {
        tag: node.tag,
        children: children,
        newlines: {
          outBefore: 1,
          outAfter: 1,
          inEnd: 1
        }
      };

    case 'tbody':
    case 'thead':
      return {
        children: children
      };

    case 'tr':
      return {
        tag: 'tr',
        children: children,
        newlines: {
          outBefore: 1,
          outAfter: 1,
          inEnd: 1
        }
      };

    case 'th':
    case 'td':
      {
        var colspan = getNumAttr(node, 'colspan');
        var colspanStr = colspan ? " colspan=" + colspan : '';
        var rowspan = getNumAttr(node, 'rowspan');
        var rowspanStr = rowspan ? " rowspan=" + rowspan : ''; // const boldChildren = node.tag === 'th' ? `[b]${children}[/b]` : children;

        return {
          tag: 'td',
          tagSuffix: "" + rowspanStr + colspanStr,
          children: children,
          keepEmpty: true,
          newlines: {
            outBefore: 1,
            outAfter: 1
          }
        };
      }

    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
      {
        var sizes = {
          h1: 150,
          h2: 140,
          h3: 130,
          h4: 120,
          h5: 110
        };
        return {
          tag: 'size',
          tagSuffix: "=" + sizes[node.tag] + "%",
          children: [{
            tag: 'b',
            children: children
          }],
          newlines: {
            outBefore: 1,
            outAfter: 1
          }
        };
      }

    case 'p':
      return {
        children: children,
        newlines: {
          outSum: 1
        }
      };

    case 'strong':
    case 'b':
      // https://github.com/dkwingsmt/hots-patchnote-transformer/issues/1
      if (children.length === 1 && children[0].toString() === 'Lt.') {
        return '';
      }

      if (children.length === 1 && children[0] === 'Morales：') {
        children[0] = '莫拉莉斯中尉：';
      }

      return {
        children: children,
        tag: 'b'
      };

    case 's':
      return {
        children: children,
        tag: 'del'
      };

    case 'u':
      return {
        children: children,
        tag: 'u'
      };

    case 'em':
    case 'i':
      return {
        children: children,
        tag: 'i'
      };

    case 'sup':
      return {
        children: children,
        tag: 'sup'
      };

    case 'sub':
      return {
        children: children,
        tag: 'sub'
      };

    case 'blockquote':
      return {
        children: children,
        tag: 'quote',
        newlines: {
          outBefore: 1,
          outAfter: 1,
          inEnd: 1
        }
      };

    case 'ul':
      {
        return {
          children: children,
          tag: 'list',
          newlines: {
            outBefore: 1,
            inEnd: 1,
            outAfter: 1
          }
        };
      }

    case 'ol':
      {
        return {
          children: children,
          tag: 'list',
          tagSuffix: '=1',
          newlines: {
            outBefore: 1,
            inEnd: 1,
            outAfter: 1
          }
        };
      }

    case 'li':
      {
        return {
          children: ['[*]'].concat(children),
          newlines: {
            outAfter: 1
          }
        };
      }

    case 'img':
      {
        var src = findAttr(node, 'src');

        if (src) {
          return {
            children: [src.value],
            tag: 'img',
            newlines: {
              outBefore: 1,
              outAfter: 1
            }
          };
        }

        return '';
      }

    case 'article':
    case 'picture':
    case 'span':
    case 'div':
    case 'font':
      return {
        children: children
      };

    case 'a':
      var href = findAttr(node, 'href'); // #xxx is just anchor so no need for special handling

      if (href && href.value.indexOf('#') !== 0) {
        return {
          children: children,
          tag: 'url',
          tagSuffix: "=" + href.value
        };
      }

      return {
        children: children
      };

    default:
      console.warn("Unhandled node type " + node.tag);
      return {
        children: children
      };
  }
} // genTreeToString recursively convert a GenerationChild to BbsCode.
// Returns [prefix, body, suffix], where prefix and suffix contains in-newlines, out-newlines and tags.
// In terms of implementation, this function does not handle trimming, but removes empty nodes.

export function genTreeToString(node) {
  if (typeof node === 'string') {
    return ['', node, '']; // Should I trim the string and put NLs into prefix and suffix?
  }

  var nodeNewlines = __assign({
    inStart: 0,
    inEnd: 0,
    outBefore: 0,
    outAfter: 0,
    outSum: 0
  }, node.newlines); // const filteredChildren: GenerationChild[] = (node.children || []).filter((child: GenerationChild) => {
  //   if (typeof child === 'string') {
  //     return child;
  //   }
  //   if (child.keepEmpty) {
  //     return true;
  //   }
  //   return child.children && child.children.filter(Boolean).length;
  // });


  var _a = _.reduce(node.children, function (_a, curChild) {
    var prevStr = _a[0],
        prevNLAfter = _a[1];

    var _b = genTreeToString(curChild),
        nextNLBefore = _b[0],
        nextStr = _b[1],
        nextNLAfter = _b[2];

    if (!nextStr && (typeof curChild === 'string' || !curChild.keepEmpty)) {
      return [prevStr, prevNLAfter];
    }

    return [collapsedConcat(prevStr, prevNLAfter, nextNLBefore, nextStr), nextNLAfter];
  }, ['', '']),
      childrenStrReturned = _a[0],
      childrenEndNL = _a[1];

  var nodeBodyStr = collapsedConcat(childrenStrReturned, childrenEndNL);
  var tagSuffix = node.tagSuffix || '';
  var startTag = node.tag ? "[" + node.tag + tagSuffix + "]" : '';
  var endTag = node.tag ? "[/" + node.tag + "]" : '';
  var outBeforeNL = nodeNewlines.outBefore;
  var outAfterNL = Math.max(nodeNewlines.outSum - nodeNewlines.outBefore, nodeNewlines.outAfter);
  return ["" + nl(outBeforeNL) + startTag + nl(nodeNewlines.inStart), nodeBodyStr, "" + nl(nodeNewlines.inEnd) + endTag + nl(outAfterNL)];
}
export function generateBbsCode(node) {
  var genTree = bbsTreeToGenerationTree(node);
  return _.trim(collapsedConcat.apply(void 0, genTreeToString(genTree)));
}